// SiteList.jsx
import React, { useState, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FormProvider } from "react-hook-form";
import { Input, Select, Pagination, SortHeader, CompanySearchModal } from "@components";
import { SitelistFormModal } from "@pages";
import { AuthContext, useAdminCompany } from "@context";
import { ROLES } from "@constants/roles";


import { useCompanyList, useSiteList } from "@hooks";
import axios_api from "@utils/axios_api";
import { toast } from 'react-toastify';


// 파일명에 사용할 수 없는 문자 제거
function sanitizeFilename(name) {
  return name.replace(/[\\/:*?"<>|]/g, '');
}

const SiteList = () => {
  const { auth, hasPermission } = useContext(AuthContext);
  const canWrite = hasPermission('SITE_WRITE');
  const canDelete = hasPermission('SITE_DELETE');
  const isAdmin = auth.role === ROLES.SUPER_ADMIN || auth.role === ROLES.ADMIN;
  // 전역 선택한 사업장 번호 (관리자)
  const { adminCompCode, adminCompName, setAdminCompCode, setAdminCompName } = useAdminCompany();
  const compCode = isAdmin ? adminCompCode : auth?.compCode; // 각 권한에 따른 실제 회사 코드
  const compName = isAdmin ? adminCompName : auth?.compName;

  const {
    methods,
    data,
    isLoading,
    isError,
    error,
    selectedItems,
    isAllChecked,
    onSubmit,
    handlePageChange,
    changePageSize,
    handleSelectAll,
    handleSelectItem,
    handleDelete,      // 삭제 로직 (이벤트X)
    refreshList,
    pageIndex,
    recordCountPerPage,
  } = useSiteList(compCode);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("search");
  const [isEdit, setIsEdit] = useState(false);
  const [selectedCompanyCode, setSelectedCompanyCode] = useState(null);
  const [selectedSiteCode, setSelectedSiteCode] = useState(0);
  const queryClient = useQueryClient();
  

  // 등록 버튼
  const handleRegister = (e) => {
    e.preventDefault();
    if (!compCode) {
      toast.warn("회사를 지정해주세요");
      return;
    }
    setIsEdit(false);
    setSelectedSiteCode(null);
    setFormModalOpen(true);
  };

  // 수정 버튼
  const handleUpdate = (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      toast.warn("수정할 항목을 선택해주세요.");
      return;
    } else if (selectedItems.length > 1) {
      toast.warn("수정은 한 개 항목만 가능합니다.");
      return;
    }
    setIsEdit(true);
    setSelectedSiteCode(Number(selectedItems[0]));
    setFormModalOpen(true);
  };

  // 삭제 버튼
  const handleDeleteCompanies = (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      toast.warn("삭제할 항목을 선택해주세요.");
      return;
    }
    if (window.confirm("선택한 사업장을 삭제하시겠습니까?")) {
      // handleDelete에는 이벤트X
      handleDelete();
    }
  };

  // 모달 성공 시 콜백
  const handleformModalSuccess = (response) => {
    toast.success(response.message);
    queryClient.invalidateQueries(["selectSiteList"]);
    refreshList();
    setFormModalOpen(false);
  };

  // 모달에서 선택한 사업장 정보
  const handleModalSuccess = (selectedCompany) => {
    if (!selectedCompany) return;

    if (modalMode === "search") {
        setSelectedCompanyCode(selectedCompany.compCode);
    } 

    setModalOpen(false);
  };

  if (isLoading) {
    return <div>로딩 중...</div>;
  }
  if (isError) {
    console.error("isError:", error);
    return <div>오류가 발생했습니다: {error?.message || "알 수 없는 에러"}</div>;
  }

  if (!isAdmin && (!auth?.compCode)) {
    return <div>로그인 정보를 불러오는 중...</div>;
  }

  return (
    <FormProvider {...methods}>
      {/* 사업장 목록 */}
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="contents-wrap">
          {/* 페이지 타이틀 */}
          <div className="page-title-wrap">
            <h2>
              <span className="icon-dot-circle"></span>
              사업장 목록
            </h2>
          </div>

          {/* 검색 영역 */}
          <div className="search-wrap">
            <div className="search-row">
              <div className="search-item">
                <label>회사</label>
                <Input
                  name="compName"
                  disabled
                  value={compName || ""}
                />
                {isAdmin && (
                  <>
                    <button
                      type="button"
                      onClick={() => { setModalOpen(true); setModalMode("search"); }}
                      className="search-icon"
                    />
                    {adminCompCode && (
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={() => { setAdminCompCode(""); setAdminCompName(""); }}
                      >
                        초기화
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* 목록 섹션 */}
          <section>
            <div className="section-tit-wrap">
              <h3 className="border-left-primary">
                &nbsp;사업장 목록
                <span className="total-txt">
                  {" "} | 총 <span className="red">{data?.totalCount || 0}</span> 건
                </span>
              </h3>
              <div className="btn-right-box">
                <ul>
                  
                  {canWrite && (
                  <li>
                    <button
                      type="button"
                      title="등록"
                      className="btn-write"
                      onClick={handleRegister}
                    >
                      등록
                    </button>
                  </li>
                  )}
                  {canWrite && (
                  <li>
                    <button
                      type="button"
                      title="수정"
                      className="btn-update"
                      onClick={handleUpdate}
                    >
                      수정
                    </button>
                  </li>
                  )}
                  {canDelete && (
                  <li>
                    <button
                      type="button"
                      title="삭제"
                      className="btn-delete"
                      onClick={handleDeleteCompanies}
                    >
                      삭제
                    </button>
                  </li>
                  )}
                </ul>
              </div>
            </div>

            <div className="table-container overflow-x-auto table-list-wrapper">
              <table className="table-list" style={{ width: "100%" }}>
                <colgroup>
                  <col style={{ width: "1%" }} />
                  <col style={{ width: "1%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "15%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "19%" }} />
                  <col style={{ width: "4%" }} />
                  <col style={{ width: "17%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={isAllChecked}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th>번호</th>
                    <SortHeader field="SITE_NAME" label="사업장명" style={{ textAlign: 'left' }} />
                    <SortHeader field="COMP_CODE" label="회사명" style={{ textAlign: 'left' }} />
                    <SortHeader field="BIZ_NO" label="사업자등록번호" style={{ textAlign: 'left' }} />
                    <SortHeader field="PHONE" label="전화번호" />
                    <SortHeader field="REP_NAME" label="대표자명" style={{ textAlign: 'left' }} />
                    <th style={{ textAlign: 'left' }}>사업장 주소</th>
                    <SortHeader field="BIZ_TYPE" label="업태" />
                    <th style={{ textAlign: 'left' }}>비고</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.resultList && data.resultList.length > 0 ? (
                    data.resultList.map((item, idx) => (
                      <tr
                        key={item.siteCode}
                        onClick={() => handleSelectItem(item.siteCode)}
                        style={{ cursor: "pointer" }}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(item.siteCode)}
                            onChange={() => handleSelectItem(item.siteCode)}
                          />
                        </td>
                        <td>{(pageIndex - 1) * recordCountPerPage + idx + 1}</td>
                        <td style={{ textAlign: 'left' }}>{item.siteName}</td>
                        <td style={{ textAlign: 'left' }}>{item.compName}</td>
                        <td style={{ textAlign: 'left' }}>{item.bizNo}</td>
                        <td>{item.phone}</td>
                        <td style={{ textAlign: 'left' }}>{item.repName}</td>
                        <td style={{ textAlign: 'left' }}>
                          {item.addr1} {item.addr2 || ""}
                        </td>
                        <td></td>
                        <td style={{ textAlign: 'left' }}>{item.notice}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="table-empty" style={{ textAlign: "center" }}>
                        조회 결과가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
              <span style={{ fontSize: '14px', whiteSpace: 'nowrap', color: '#555' }}>페이지당 행 수</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto' }}
                value={recordCountPerPage}
                onChange={(e) => changePageSize(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {data?.paginationInfo && data.paginationInfo.totalRecordCount > 0 && (
              <Pagination
                totalPages={Math.ceil(
                  data.paginationInfo.totalRecordCount /
                    data.paginationInfo.recordCountPerPage
                )}
                currentPage={pageIndex}
                onPageChange={handlePageChange}
                pageRangeDisplayed={10}
              />
            )}
          </section>
        </div>
      </form>
      {isAdmin && (
              <CompanySearchModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onConfirm={handleModalSuccess}
              />
            )}

      {/* 등록/수정 모달 */}
      <SitelistFormModal
        isOpen={formModalOpen}
        onClose={() => setFormModalOpen(false)}
        isEdit={isEdit}
        boardNum={isEdit ? selectedSiteCode : compCode}
        onSuccess={handleformModalSuccess}
        isAdmin={isAdmin}
      />

      
    </FormProvider>
  );
};

export { SiteList };
